import {Form, FormField, FormSubmitHandler} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {z} from 'zod';

import {useState} from 'react';

import {uniq} from 'ramda';

import i18n from '@dms/i18n';
import {SettingsFooter} from '@dms/settings/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

const OAUTH_CLIENT_ID_PATTERN = /^[a-z-]{5,}$/;

const oauthClientFormSchema = z.object({
  clientType: z.enum(['public', 'confidential']),
  name: z.string().min(1),
  clientId: z.string().regex(OAUTH_CLIENT_ID_PATTERN),
  redirectUris: z.array(z.string()).min(1),
});

export type OAuthClientFormValues = z.infer<typeof oauthClientFormSchema>;

const CLIENT_TYPE_OPTIONS = [
  {value: 'public', label: i18n.t('entity.oauthClient.labels.publicClient')},
  {value: 'confidential', label: i18n.t('entity.oauthClient.labels.confidentialClient')},
];

interface OAuthClientFormProps extends RequiredTestIdProps {
  mode: 'create' | 'edit';
  isReadOnly?: boolean;
  // The client type of a system client cannot be derived from the API response
  // (its clientSecret is never returned), so the detail page hides the field.
  isClientTypeHidden?: boolean;
  defaultValues: Partial<OAuthClientFormValues>;
  submitButtonTitle: string;
  onSubmit: (values: OAuthClientFormValues) => Promise<void>;
  onCancel: () => void;
}

export function OAuthClientForm(props: OAuthClientFormProps) {
  const [redirectUriOptions, setRedirectUriOptions] = useState<string[]>(
    props.defaultValues.redirectUris ?? []
  );

  const isCreate = props.mode === 'create';
  const isReadOnly = props.isReadOnly ?? false;

  const handleSubmit: FormSubmitHandler<OAuthClientFormValues> = async (values) => {
    await props.onSubmit(values);
  };

  return (
    <Form<OAuthClientFormValues>
      experimentalZodSchema={oauthClientFormSchema}
      defaultValues={props.defaultValues}
      onSubmit={handleSubmit}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <Show when={!props.isClientTypeHidden}>
            <FormField
              control={control}
              name="clientType"
              type="radio"
              isRequired
              isDisabled={!isCreate || isReadOnly}
              options={CLIENT_TYPE_OPTIONS}
              label={i18n.t('entity.oauthClient.labels.clientType')}
              helperText={
                isCreate ? i18n.t('entity.oauthClient.labels.clientTypeHelper') : undefined
              }
              data-testid={suffixTestId('clientType', props)}
            />
          </Show>
          <FormField
            control={control}
            name="name"
            type="text"
            isRequired
            isDisabled={isReadOnly}
            label={i18n.t('general.labels.name')}
            data-testid={suffixTestId('name', props)}
          />
          <FormField
            control={control}
            name="clientId"
            type="text"
            isRequired
            isDisabled={isReadOnly}
            label={i18n.t('general.labels.clientId')}
            helperText={i18n.t('entity.oauthClient.labels.clientIdHelper')}
            data-testid={suffixTestId('clientId', props)}
          />
          <FormField
            control={control}
            name="redirectUris"
            type="creatableMultiChoice"
            isRequired
            isDisabled={isReadOnly}
            options={redirectUriOptions.map((uri) => ({label: uri, value: uri}))}
            label={i18n.t('entity.oauthClient.labels.redirectUris')}
            helperText={i18n.t('entity.oauthClient.labels.redirectUrisHelper')}
            noOptionsMessage={() => i18n.t('entity.oauthClient.labels.redirectUrisTypeToAdd')}
            onCreateOption={(inputValue) => {
              const currentUris = formApi.getValues('redirectUris');
              formApi.setValue('redirectUris', uniq([...(currentUris ?? []), inputValue]), {
                shouldValidate: true,
              });
              setRedirectUriOptions((previousOptions) => uniq([...previousOptions, inputValue]));
            }}
            data-testid={suffixTestId('redirectUris', props)}
          />
          <Show when={!isReadOnly}>
            <SettingsFooter
              actions={[
                {
                  type: 'button',
                  variant: 'secondary',
                  title: i18n.t('general.actions.cancel'),
                  onClick: props.onCancel,
                  'data-testid': suffixTestId('cancel', props),
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  variant: 'primary',
                  title: props.submitButtonTitle,
                  'data-testid': suffixTestId('submit', props),
                },
              ]}
              data-testid={suffixTestId('footer', props)}
            />
          </Show>
        </VStack>
      )}
    </Form>
  );
}
